import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const RefreshBanner = ({ userCredits, currentUser, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Check if user is newly registered (within last 15 minutes)
  const isNewUser = () => {
    if (!currentUser || !currentUser.metadata?.creationTime) {
      return false;
    }

    const creationTime = new Date(currentUser.metadata.creationTime);
    const now = new Date();
    const timeDiff = now - creationTime;
    const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds

    return timeDiff <= fifteenMinutes;
  };

  // Check localStorage for dismissed state
  useEffect(() => {
    if (currentUser) {
      const dismissedKey = `refresh-banner-dismissed-${currentUser.uid}`;
      const isDismissed = localStorage.getItem(dismissedKey) === 'true';
      setBannerDismissed(isDismissed);
    }
  }, [currentUser]);

  // Determine if banner should be visible
  useEffect(() => {
    const shouldShow = (
      userCredits === 0 &&
      isNewUser() &&
      !bannerDismissed &&
      currentUser
    );

    setIsVisible(shouldShow);

    // Auto-hide when credits appear
    if (userCredits > 0 && isVisible) {
      setIsVisible(false);
    }
  }, [userCredits, currentUser, bannerDismissed, isVisible]); // isNewUser is a function, not a dependency

  const handleDismiss = () => {
    if (currentUser) {
      const dismissedKey = `refresh-banner-dismissed-${currentUser.uid}`;
      localStorage.setItem(dismissedKey, 'true');
      setBannerDismissed(true);
      setIsVisible(false);

      if (onDismiss) {
        onDismiss();
      }
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-center flex-1">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-lg">👋</span>
            </div>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-gray-900">
              Welcome to PDF Statement Converter!
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Thanks for registering! If your 5 free credits don't appear within a few seconds,
              simply refresh the page to see them.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-3 py-1.5 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Refresh Page
          </button>

          <button
            onClick={handleDismiss}
            className="inline-flex items-center justify-center h-6 w-6 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
          >
            <span className="sr-only">Dismiss</span>
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefreshBanner;